import { useState, useEffect } from 'react';
import { Eye, Check, X, Mail, FileText, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { useTheme } from '@/context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Applications = () => {
  const { isDark } = useTheme();
  const [filter, setFilter] = useState('all');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/team-requests`);
      const teamRequests = response.data || [];
      
      // Map team requests to applications format
      const mappedApplications = teamRequests.map(req => ({
        id: req.id,
        applicantName: req.name,
        email: req.contact,
        projectName: 'Loading...', // Will fetch project name separately
        projectId: req.project_id,
        role: req.roles_interested,
        status: req.status || 'Pending',
        appliedAt: req.created_at ? new Date(req.created_at).toLocaleDateString('en-GB') : 'N/A',
        linkedin: req.linkedin || '',
        experience: 'N/A',
        priority: 'medium',
      }));

      // Fetch project names for each application
      const applicationsWithProjects = await Promise.all(
        mappedApplications.map(async (app) => {
          try {
            const projectRes = await axios.get(`${API}/projects/${app.projectId}`);
            app.projectName = projectRes.data?.name || 'Unknown Project';
          } catch {
            app.projectName = 'Unknown Project';
          }
          return app;
        })
      );

      setApplications(applicationsWithProjects);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications =
    filter === 'all'
      ? applications
      : applications.filter(app => app.status?.toLowerCase() === filter.toLowerCase());

  const handleShortlist = async (id) => {
    try {
      // TODO: Implement shortlist endpoint in backend
      await axios.patch(`${API}/team-requests/${id}`, { status: 'Shortlisted' });
      fetchApplications(); // Refresh data
    } catch (error) {
      console.error('Error shortlisting application:', error);
    }
  };

  const handleReject = async (id) => {
    try {
      // TODO: Implement reject endpoint in backend
      await axios.patch(`${API}/team-requests/${id}`, { status: 'Rejected' });
      fetchApplications(); // Refresh data
    } catch (error) {
      console.error('Error rejecting application:', error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Applications</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Review and manage project applications</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className={`w-full sm:w-40 ${
            isDark 
              ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30 text-purple-200' 
              : 'bg-white border-gray-300'
          }`}>
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30' : ''}>
            <SelectItem value="all" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>All Applications</SelectItem>
            <SelectItem value="pending" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Pending</SelectItem>
            <SelectItem value="shortlisted" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Shortlisted</SelectItem>
            <SelectItem value="rejected" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Applications</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{applications.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending Review</p>
          <p className="text-2xl font-bold text-orange-600">
            {applications.filter(a => a.status === 'Pending').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Shortlisted</p>
          <p className="text-2xl font-bold text-green-600">
            {applications.filter(a => a.status === 'Shortlisted').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Rejected</p>
          <p className="text-2xl font-bold text-red-600">
            {applications.filter(a => a.status === 'Rejected').length}
          </p>
        </div>
      </div>

      {/* Applications - Desktop Table / Mobile Cards */}
      {loading ? (
        <div className={`rounded-xl shadow-md border p-8 sm:p-12 text-center transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading applications...</p>
        </div>
      ) : (
        <div className={`rounded-xl shadow-md border overflow-hidden transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ID</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>APPLICANT</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>PROJECT</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ROLE</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>EXPERIENCE</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>STATUS</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>APPLIED AT</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredApplications.map((app) => (
                  <tr
                    key={app.id}
                    className={`border-b transition-colors group ${
                      isDark 
                        ? 'border-purple-500/20 hover:bg-purple-500/10' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <TableCell className={`font-medium transition-colors ${
                      isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                    }`}>{app.id}</TableCell>
                    <TableCell>
                      <div>
                        <p className={`font-medium transition-colors ${
                          isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                        }`}>{app.applicantName}</p>
                        <p className={`text-xs transition-colors ${
                          isDark ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-500'
                        }`}>{app.email}</p>
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.projectName}</TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.role}</TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.experience}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          app.status === 'Shortlisted'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : app.status === 'Rejected'
                            ? isDark
                              ? 'bg-red-500/30 text-red-200'
                              : 'bg-red-100 text-red-700'
                            : isDark
                              ? 'bg-orange-500/30 text-orange-200'
                              : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {app.status}
                      </span>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{app.appliedAt}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <FileText className="w-4 h-4" />
                        </Button>
                        {app.status === 'Pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 w-8 p-0 ${
                                isDark 
                                  ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                                  : 'text-green-600'
                              }`}
                              onClick={() => handleShortlist(app.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 w-8 p-0 ${
                                isDark 
                                  ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                                  : 'text-red-600'
                              }`}
                              onClick={() => handleReject(app.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-purple-500/20">
            {filteredApplications.map((app) => (
              <div
                key={app.id}
                className={`p-4 transition-colors ${
                  isDark 
                    ? 'hover:bg-purple-500/10' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-semibold transition-colors ${
                        isDark ? 'text-purple-300' : 'text-purple-600'
                      }`}>{app.id.substring(0, 8)}...</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          app.status === 'Shortlisted'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : app.status === 'Rejected'
                            ? isDark
                              ? 'bg-red-500/30 text-red-200'
                              : 'bg-red-100 text-red-700'
                            : isDark
                              ? 'bg-orange-500/30 text-orange-200'
                              : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {app.status}
                      </span>
                    </div>
                    <h3 className={`font-semibold mb-1 transition-colors ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{app.applicantName}</h3>
                    <p className={`text-xs mb-2 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{app.email}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                      isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                    }`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                      isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                    }`}>
                      <FileText className="w-3.5 h-3.5" />
                    </Button>
                    {app.status === 'Pending' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${
                            isDark 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                              : 'text-green-600'
                          }`}
                          onClick={() => handleShortlist(app.id)}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${
                            isDark 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                              : 'text-red-600'
                          }`}
                          onClick={() => handleReject(app.id)}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Project</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{app.projectName}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Role</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{app.role}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Experience</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{app.experience}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Applied At</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{app.appliedAt}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;

